import type { User } from '@/models/User';
import CoreApi from '@/api/CoreApi';
import i18nKeys from '@/helpers/I18nHelper';
import type { PermissionsResponse } from '@/models/Roles';

/**
 * Loads user profile data.
 * @returns A promise that resolves with the user's profile data.
 */
export async function loadUserProfile(): Promise<User> {
  try {
    const response = await CoreApi.axiosClient.get('/user/profile');
    const user: User = response.data;
    await CoreApi.authStore.setUser(user);
    await CoreApi.authStore.setLanguage(user.language);
    return user;
  } catch {
    await CoreApi.authStore.logoutAndClearUserData();
    throw new Error(i18nKeys.userDataError);
  }
}

/**
 * Patched the user profile language.
 * @returns A promise that resolves with a successful axios response.
 */
export async function patchUserLanguage(user: User): Promise<void> {
  try {
    await CoreApi.axiosClient.patch('/user/language', {
      language: user.language,
    });
    await CoreApi.authStore.setUser(user);
  } catch (error) {
    console.error(error);
    throw new Error(i18nKeys.userDataError);
  }
}

/**
 * Patched the user profile details.
 * @returns A promise that resolves with a successful axios response.
 */
export async function patchUserDetails(user: User): Promise<void> {
  try {
    await CoreApi.axiosClient.patch('/user/profile', user);
    await CoreApi.authStore.setUser(user);
  } catch (error) {
    console.error(error);
    throw new Error(i18nKeys.userDataError);
  }
}

/**
 * Get the user permissions and role.
 * @returns A promise that resolves with a successful axios response.
 */
export async function getUserPermissions(): Promise<void> {
  const response = await CoreApi.axiosClient.get('/user/permissions');
  const { permissions, role } = response.data as PermissionsResponse;
  CoreApi.authStore.setPermissions(permissions, role);
}

/**
 * Checks whether the current user has accepted the terms.
 * @returns A promise resolving to a boolean indicating acceptance.
 */
export async function getUserTermsAccepted(): Promise<boolean> {
  try {
    const response = await CoreApi.axiosClient.get('/user/terms');
    return Boolean(response.data);
  } catch {
    throw new Error(i18nKeys.userDataError);
  }
}

/**
 * Marks the current user's terms as accepted.
 */
export async function acceptUserTerms(): Promise<void> {
  try {
    await CoreApi.axiosClient.put('/user/terms');
  } catch {
    throw new Error(i18nKeys.userDataError);
  }
}
